import React, {useState} from 'react';
import {BrowserRouter, Navigate, NavLink, Route, Routes} from 'react-router-dom';
import {BookOpen, Calendar, Gamepad2, Menu, MessageCircleMore, Search, X} from 'lucide-react';
import 'tailwindcss/tailwind.css';
import TodayWordLoader from './components/TodayWordLoader';
import TypingGame from './components/TypingGame';
import WordSearch from './components/WordSearch';
import WordsByLevel from './components/WordsByLevel';
import UserLogin from './components/UserLogin';
import AiChatBot from './components/AiChatBot';
import {ThemeProvider} from './features/theme/ThemeContext';
import ThemeToggle from './features/theme/ThemeToggle';

const navItems = [
    {path: '/vocabulary', label: '단어 공부', icon: BookOpen},
    {path: '/chat', label: 'AI 채팅', icon: MessageCircleMore},
    {path: '/daily', label: '오늘의 단어', icon: Calendar},
    {path: '/search', label: '단어 검색', icon: Search},
    {path: '/typing', label: '타자 연습', icon: Gamepad2},
];

const desktopNavClass = ({isActive}: { isActive: boolean }) =>
    `px-4 py-2 rounded-full flex items-center gap-2 text-sm font-medium transition-colors ${
        isActive
            ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-300'
            : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
    }`;

const mobileNavClass = ({isActive}: { isActive: boolean }) =>
    `p-3 rounded-lg flex items-center gap-3 text-sm font-medium ${
        isActive
            ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-300'
            : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800'
    }`;

function AppLayout() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    return (
        <div className="min-h-screen flex flex-col bg-gray-50 text-gray-900 font-sans transition-colors duration-300 dark:bg-gray-900 dark:text-gray-100">
            <header className="bg-white shadow-sm sticky top-0 z-50 dark:bg-gray-800 dark:border-b dark:border-gray-700">
                <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                    <NavLink
                        to="/daily"
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        <div className="bg-indigo-100 p-2 rounded-xl transition-colors dark:bg-indigo-900">
                            <BookOpen className="w-6 h-6 text-indigo-600 dark:text-indigo-300"/>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-lg font-extrabold text-gray-800 leading-none dark:text-gray-100">일본어 공부방</span>
                            <span className="text-xs text-gray-500 font-medium dark:text-gray-400">매일 조금씩 성장하는 공간</span>
                        </div>
                    </NavLink>

                    <nav className="hidden md:flex items-center gap-1">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={desktopNavClass}
                                >
                                    <Icon className="w-4 h-4"/>
                                    {item.label}
                                </NavLink>
                            );
                        })}
                    </nav>

                    <div className="flex items-center gap-3">
                        <ThemeToggle />
                        
                        <UserLogin/>

                        <button
                            className="md:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:bg-gray-700"
                            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                            aria-label={isMobileMenuOpen ? '메뉴 닫기' : '메뉴 열기'}
                        >
                            {isMobileMenuOpen ? <X className="w-6 h-6"/> : <Menu className="w-6 h-6"/>}
                        </button>
                    </div>
                </div>

                {isMobileMenuOpen && (
                    <div className="md:hidden border-t border-gray-100 bg-white absolute w-full shadow-lg dark:bg-gray-800 dark:border-gray-700">
                        <div className="flex flex-col p-4 space-y-2">
                            {navItems.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className={mobileNavClass}
                                    >
                                        <Icon className="w-5 h-5"/>
                                        {item.label}
                                    </NavLink>
                                );
                            })}
                        </div>
                    </div>
                )}
            </header>

            <main className="container mx-auto px-4 py-8 flex-1">
                <Routes>
                    <Route path="/" element={<Navigate to="/daily" replace/>}/>
                    <Route path="/vocabulary" element={<WordsByLevel/>}/>
                    <Route path="/chat" element={<AiChatBot/>}/>
                    <Route path="/daily" element={<TodayWordLoader/>}/>
                    <Route path="/search" element={<WordSearch/>}/>
                    <Route path="/typing" element={<TypingGame/>}/>
                    <Route path="*" element={<Navigate to="/daily" replace/>}/>
                </Routes>
            </main>

            <footer className="bg-white border-t border-gray-200 mt-auto dark:bg-gray-800 dark:border-gray-700">
                <div className="container mx-auto px-4 py-6 text-center text-gray-500 text-sm dark:text-gray-400">
                    &copy; 2026 JWord. All rights reserved.
                </div>
            </footer>
        </div>
    );
}

export default function App() {
    return (
        <ThemeProvider>
            <BrowserRouter>
                <AppLayout/>
            </BrowserRouter>
        </ThemeProvider>
    );
}
